import axiosInstance from "./axiosInstance";
import { Mumeen } from "../types/itsDetails";

export interface CreateUserPayload {
  userCode: string;
  fullName: string;
  email: string;
  plainPassword: string; 
  mobileNo: string;
  whatsAppNumber: string;
  itsNo: string;
  status: string;
  validityStartDate: string;
  validityEndDate: string | null;
  canRequestUser: boolean;
  venueId: number | null;
  miqaatId: number | null;
  roleIds: number[];
  createdBy: number;
  isNonItsUser: boolean;
}

export interface UpdateUserPayload {
  userCode: string;
  fullName: string;
  email: string;
  plainPassword: string;
  mobileNo: string;
  whatsAppNumber: string;
  itsNo: string;
  status: string;
  validityStartDate: string;
  validityEndDate: string | null;
  canRequestUser: boolean;
  venueId: number | null;
  miqaatId: number | null;
  roleIds: number[];
  isNonItsUser: boolean;
}

export interface RoleResponse {
  roleId: number;
  roleName: string;
  roleDescription: string;
  isActive: boolean;
  isAvailableForDelegation: boolean;
}

export interface DashboardSummary {
  totalUsers: number;
  activeUsers: number;
  totalRoles: number;
  totalMenus: number;
  totalCountries: number;
  totalCities: number;
  activeCutoffConfigurations: number;
}

export interface VenueResponse {
  venueId: number;
  venueName: string;
  contactNo: string;
  emailAddress: string;
  description: string;
  timeZone: string;
  isActive: boolean;
}

export interface VenueListResponse {
  items: VenueResponse[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
  pageNumber: number;
}

export interface RequestUserPayload {
  userCode: string;
  itsNo: string;
  fullName: string;
  mobileNo: string;
  whatsAppNumber: string;
  email: string;
  requestRemark: string;
  validityStartDate: string;
  validityEndDate: string;
  venueId: number;
  miqaatId: number;
  roleIds: number[];
}
export interface MiqaatResposne {
  miqaatId: number;
  miqaatCode: string;
  miqaatName: string;
  description: string;
  isActive: boolean;
}

export const getAuthHeader = () => { 
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getConsolidatedPermissions = async (userId: number | string) => {
  try {
    const res = await axiosInstance.get(`/User/${userId}/consolidated-permissions`, {
      headers: getAuthHeader(),
    });
    return res.data;
  } catch (error) {
    console.error("Error fetching consolidated permissions:", error);
    throw error;
  }
};

// GET USERS WITH PAGINATION
export const getUsers = async (pageNumber = 1, pageSize = 10) => {
  const res = await axiosInstance.get("/User/users", {
    params: { pageNumber, pageSize },
    headers: getAuthHeader(),
  });

  return res.data;
};

// GET USER BY ID
export const getUserById = async (userId: number) => {
  const res = await axiosInstance.get(`/User/${userId}`, {
    headers: getAuthHeader(),
  });

  return res.data;
};

// CREATE USER
export const createUser = async (payload: CreateUserPayload) => {
  const res = await axiosInstance.post("/User", payload, {
    headers: getAuthHeader(),
  });

  return res.data;
};

// UPDATE USER
export const updateUser = async (userId: number, payload: UpdateUserPayload) => {
  const res = await axiosInstance.put(`/User/${userId}`, payload, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getDetailsByITS = async (
  itsNo: string,
  force: boolean,
): Promise<Mumeen | null> => {
  try {
    const response = await axiosInstance.get(`/its/${itsNo}?force=${force}`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    return response.data;
  } catch (error) {
    return null;
  }
};

// TOGGLE USER ACTIVE STATUS
export const toggleUserStatus = async (
  userId: number,
  isActive: boolean,
) => {
  const res = await axiosInstance.patch(
    `/User/${userId}`,
    undefined,
    {
      params: {
        isActive,
      },
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

// DELETE USER (soft delete by setting status)
export const deleteUser = async (userId: number, updatedBy: number) => {
  const payload = {
    userId,
    status: "Inactive",
    updatedBy,
  };

  const res = await axiosInstance.put(`/User/${userId}`, payload,{
    headers: getAuthHeader(),
  });
  return res.data;
};

// GET USER WITH ROLES
export const getUserWithRoles = async (userId: number) => {
  const res = await axiosInstance.get(`/User/${userId}/with-roles`, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getRoles = async (pageNumber = 1, pageSize = 100) => {
  const res = await axiosInstance.get("/Role/roles", {
    params: { pageNumber, pageSize },
    headers: getAuthHeader(),
  });
  return res.data;
};

// SOFT DELETE USER 
export const softDeleteUser = async (userId: number) => {
  const res = await axiosInstance.put(`/User/${userId}/Soft-Delete`, {
    headers: getAuthHeader(),
  });
  return res.data; 
};

export const getDashboardSummary = async (): Promise<DashboardSummary> => {
  const res = await axiosInstance.get("/User/dashboard-summary", {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getVenues = async (
  pageNumber = 1,
  pageSize = 100
): Promise<VenueListResponse> => {
  const res = await axiosInstance.get("/Venues/list", {
    params: { pageNumber, pageSize },
    headers: getAuthHeader(),
  });

  return res.data;
};

export const requestUser = async (payload: RequestUserPayload) => {
  const res = await axiosInstance.post("/User/request-a-user", payload, {
    headers: getAuthHeader(),
  });
  return res.data;
};

export const getMiqaats = async (): Promise<MiqaatResposne[]> => {
  const res = await axiosInstance.get("/miqaat", {
    headers: getAuthHeader(),
  });
  return res.data;
};